/**
 * Track E — photo color-layer engine (pure; runs in worker and vitest/node).
 * Port of the Python photo workflow: grid mapping, area-average resample,
 * transparent/near-white skipping, median-cut quantization, Floyd–Steinberg
 * serpentine dithering, adaptive vs LightBurn-exact palette modes, layer
 * sorting, square-run optimization. See docs/recon/photo-engine.json.
 */
import type {
  CutParams,
  LayerSortMode,
  MopaLayerRecord,
  PaletteMode,
  PhotoPaletteEntry,
  PhotoResult,
  PhotoSettings,
  RawImage,
  SwatchEntry,
  SwatchLibrary,
} from "../core/types";
import { LIGHTBURN_PALETTE } from "../core/types";

/**
 * Progress + cancel callbacks for the photo engine. Backward-compatible: every
 * field is optional and the old two-argument onProgress call still type-checks.
 * The optional third `pixelCount` argument lets the worker forward a real cell
 * count into ProgressMsg.pixelCount (was hard-0). `fraction` is a MONOTONIC
 * overall fraction in 0..1, or -1 for a genuinely indeterminate stage.
 */
export interface PhotoCallbacks {
  onProgress?(stage: string, fraction: number, pixelCount?: number): void;
  isCancelled?(): boolean;
}

export class PhotoCancelled extends Error {}

/**
 * PhotoResult plus the calibrated per-color MOPA parameter records. Returned by
 * generatePhotoLayersCalibrated; a plain PhotoResult (its supertype) is what the
 * worker/UI consume when calibration is not active, so nothing downstream breaks.
 */
export interface CalibratedPhotoResult extends PhotoResult {
  /** One record per emitted, strictly non-overlapping color layer, in run order. */
  mopaRecords: MopaLayerRecord[];
}

// The TS PhotoSettings drops the Python per-run threshold fields; these are the
// verified defaults from the original app (alpha_threshold=12, white=245).
const ALPHA_THRESHOLD = 12;
const WHITE_THRESHOLD = 245;

type RGB = [number, number, number];

const LB_RGB: RGB[] = LIGHTBURN_PALETTE.map(hexToRgb);
const LB_INDEX_BY_HEX = new Map<string, number>(
  LIGHTBURN_PALETTE.map((hex, i) => [hex.toUpperCase(), i]),
);

// ---------- color helpers ----------

function clampInt(lo: number, hi: number, v: number): number {
  v = Math.floor(v);
  return v < lo ? lo : v > hi ? hi : v;
}

function chan(v: number): number {
  v = Math.round(v);
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

function rgbToHex(rgb: RGB): string {
  const r = chan(rgb[0]).toString(16).padStart(2, "0");
  const g = chan(rgb[1]).toString(16).padStart(2, "0");
  const b = chan(rgb[2]).toString(16).padStart(2, "0");
  return ("#" + r + g + b).toUpperCase();
}

function hexToRgb(hex: string): RGB {
  const h = hex.trim().replace(/^#/, "");
  return [
    parseInt(h.slice(0, 2), 16) || 0,
    parseInt(h.slice(2, 4), 16) || 0,
    parseInt(h.slice(4, 6), 16) || 0,
  ];
}

/** Rec.709 luminance on the 0..255 scale (used for palette sorting). */
function luminance(rgb: RGB): number {
  return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
}

/** HSV hue in [0,1), matching Python's colorsys.rgb_to_hsv (0 for grays). */
function hue(rgb: RGB): number {
  const r = rgb[0] / 255;
  const g = rgb[1] / 255;
  const b = rgb[2] / 255;
  const mx = Math.max(r, g, b);
  const mn = Math.min(r, g, b);
  if (mx === mn) return 0;
  const d = mx - mn;
  const rc = (mx - r) / d;
  const gc = (mx - g) / d;
  const bc = (mx - b) / d;
  let h: number;
  if (r === mx) h = bc - gc;
  else if (g === mx) h = 2 + rc - bc;
  else h = 4 + gc - rc;
  h = (h / 6) % 1;
  if (h < 0) h += 1;
  return h;
}

/** Luma-weighted squared RGB distance (matching the Python 0.30/0.59/0.11 metric). */
function dist2(a: RGB, b: RGB): number {
  const dr = a[0] - b[0];
  const dg = a[1] - b[1];
  const db = a[2] - b[2];
  return 0.3 * dr * dr + 0.59 * dg * dg + 0.11 * db * db;
}

function commas(n: number): string {
  return Math.round(n)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

// ---------- area-average resample ----------

/**
 * Cooperative work hooks threaded into the heavy loops. `onRow(fraction01)` is
 * called periodically with this stage's local 0..1 progress; `isCancelled()`
 * lets a long resample/dither bail promptly. Both optional — omitting them
 * preserves the original tight-loop behavior for tests and callers that don't
 * need progress.
 */
interface WorkHooks {
  onRow?(fraction01: number): void;
  isCancelled?(): boolean;
}

/**
 * Deterministic box/area-average resample of an RGBA image to cols x rows.
 * DEVIATION: the Python app used Pillow LANCZOS; area averaging is close enough
 * for photo layering and produces stable, testable output with no dependency.
 */
function resampleAreaAverage(
  src: Uint8ClampedArray,
  srcW: number,
  srcH: number,
  cols: number,
  rows: number,
  hooks?: WorkHooks,
): Uint8ClampedArray {
  const out = new Uint8ClampedArray(cols * rows * 4);
  const scaleX = srcW / cols;
  const scaleY = srcH / rows;
  const onRow = hooks?.onRow;
  const isCancelled = hooks?.isCancelled;
  for (let row = 0; row < rows; row++) {
    if (isCancelled && (row & 31) === 0 && isCancelled()) {
      throw new PhotoCancelled("Photo layer generation cancelled.");
    }
    if (onRow && (row & 31) === 0) onRow(rows > 0 ? row / rows : 1);
    const sy0 = row * scaleY;
    const sy1 = (row + 1) * scaleY;
    const iy0 = Math.floor(sy0);
    const iy1 = Math.min(srcH, Math.ceil(sy1));
    for (let col = 0; col < cols; col++) {
      const sx0 = col * scaleX;
      const sx1 = (col + 1) * scaleX;
      const ix0 = Math.floor(sx0);
      const ix1 = Math.min(srcW, Math.ceil(sx1));
      let sr = 0;
      let sg = 0;
      let sb = 0;
      let sa = 0;
      let sw = 0;
      for (let yy = iy0; yy < iy1; yy++) {
        const wy = Math.min(sy1, yy + 1) - Math.max(sy0, yy);
        if (wy <= 0) continue;
        const rowBase = yy * srcW;
        for (let xx = ix0; xx < ix1; xx++) {
          const wx = Math.min(sx1, xx + 1) - Math.max(sx0, xx);
          if (wx <= 0) continue;
          const w = wx * wy;
          const p = (rowBase + xx) * 4;
          sr += src[p] * w;
          sg += src[p + 1] * w;
          sb += src[p + 2] * w;
          sa += src[p + 3] * w;
          sw += w;
        }
      }
      const o = (row * cols + col) * 4;
      if (sw > 0) {
        out[o] = Math.round(sr / sw);
        out[o + 1] = Math.round(sg / sw);
        out[o + 2] = Math.round(sb / sw);
        out[o + 3] = Math.round(sa / sw);
      }
    }
  }
  return out;
}

// ---------- median cut ----------

interface HistEntry {
  r: number;
  g: number;
  b: number;
  c: number;
}

interface SourceColor {
  rgb: RGB;
  count: number;
}

function chanOf(e: HistEntry, ch: number): number {
  return ch === 0 ? e.r : ch === 1 ? e.g : e.b;
}

function cmpEntry(a: HistEntry, b: HistEntry, ch: number): number {
  let d = chanOf(a, ch) - chanOf(b, ch);
  if (d !== 0) return d;
  d = a.r - b.r;
  if (d !== 0) return d;
  d = a.g - b.g;
  if (d !== 0) return d;
  return a.b - b.b;
}

/** Longest single-channel range of a box, and the winning channel (r>g>b on ties). */
function boxRange(es: HistEntry[]): { range: number; ch: number } {
  let rmin = 255;
  let rmax = 0;
  let gmin = 255;
  let gmax = 0;
  let bmin = 255;
  let bmax = 0;
  for (const e of es) {
    if (e.r < rmin) rmin = e.r;
    if (e.r > rmax) rmax = e.r;
    if (e.g < gmin) gmin = e.g;
    if (e.g > gmax) gmax = e.g;
    if (e.b < bmin) bmin = e.b;
    if (e.b > bmax) bmax = e.b;
  }
  const rr = rmax - rmin;
  const gr = gmax - gmin;
  const br = bmax - bmin;
  let range = rr;
  let ch = 0;
  if (gr > range) {
    range = gr;
    ch = 1;
  }
  if (br > range) {
    range = br;
    ch = 2;
  }
  return { range, ch };
}

function boxCount(es: HistEntry[]): number {
  let t = 0;
  for (const e of es) t += e.c;
  return t;
}

function boxMean(es: HistEntry[]): RGB {
  let sr = 0;
  let sg = 0;
  let sb = 0;
  let sc = 0;
  for (const e of es) {
    sr += e.r * e.c;
    sg += e.g * e.c;
    sb += e.b * e.c;
    sc += e.c;
  }
  if (sc <= 0) return [0, 0, 0];
  return [Math.round(sr / sc), Math.round(sg / sc), Math.round(sb / sc)];
}

/** Classic median cut over the visible-pixel histogram; box color = count-weighted mean. */
function buildSourcePalette(
  samples: Uint8Array | Uint8ClampedArray,
  sampleCount: number,
  maxColors: number,
): SourceColor[] {
  if (sampleCount <= 0) return [];

  const hist = new Map<number, HistEntry>();
  for (let i = 0; i < sampleCount; i++) {
    const p = i * 3;
    const r = samples[p];
    const g = samples[p + 1];
    const b = samples[p + 2];
    const key = (r << 16) | (g << 8) | b;
    const e = hist.get(key);
    if (e) e.c++;
    else hist.set(key, { r, g, b, c: 1 });
  }

  const distinct = hist.size;
  const colors = Math.max(1, Math.min(30, Math.floor(maxColors), distinct));

  if (colors === 1) {
    let sr = 0;
    let sg = 0;
    let sb = 0;
    for (const e of hist.values()) {
      sr += e.r * e.c;
      sg += e.g * e.c;
      sb += e.b * e.c;
    }
    const avg: RGB = [
      Math.trunc(sr / sampleCount),
      Math.trunc(sg / sampleCount),
      Math.trunc(sb / sampleCount),
    ];
    return [{ rgb: avg, count: sampleCount }];
  }

  let boxes: HistEntry[][] = [Array.from(hist.values())];
  while (boxes.length < colors) {
    let bi = -1;
    let bestRange = 0;
    let bestCount = -1;
    for (let k = 0; k < boxes.length; k++) {
      const es = boxes[k];
      if (es.length < 2) continue;
      const { range } = boxRange(es);
      if (range <= 0) continue;
      const cnt = boxCount(es);
      if (range > bestRange || (range === bestRange && cnt > bestCount)) {
        bestRange = range;
        bestCount = cnt;
        bi = k;
      }
    }
    if (bi < 0) break;

    const es = boxes[bi];
    const { ch } = boxRange(es);
    es.sort((a, b) => cmpEntry(a, b, ch));
    const total = boxCount(es);
    const half = total / 2;
    let acc = 0;
    let split = 1;
    for (let i = 0; i < es.length; i++) {
      acc += es[i].c;
      if (acc >= half) {
        split = i + 1;
        break;
      }
    }
    if (split < 1) split = 1;
    if (split > es.length - 1) split = es.length - 1;
    boxes.splice(bi, 1, es.slice(0, split), es.slice(split));
  }

  const out: SourceColor[] = boxes.map((es) => ({ rgb: boxMean(es), count: boxCount(es) }));
  out.sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    if (a.rgb[0] !== b.rgb[0]) return a.rgb[0] - b.rgb[0];
    if (a.rgb[1] !== b.rgb[1]) return a.rgb[1] - b.rgb[1];
    return a.rgb[2] - b.rgb[2];
  });
  return out.slice(0, colors);
}

// ---------- palette choice + sort ----------

interface PalettePair {
  svg: RGB;
  target: RGB;
  count: number;
}

function choosePalette(
  source: SourceColor[],
  maxColors: number,
  paletteMode: PaletteMode,
  startLayer: number,
): PalettePair[] {
  const mc = Math.max(1, Math.min(30, Math.floor(maxColors)));
  const src = source.slice(0, mc);
  const isLightburn = (paletteMode || "").toLowerCase().includes("lightburn");

  if (!isLightburn) {
    return src.map((s) => ({ svg: s.rgb, target: s.rgb, count: s.count }));
  }

  const sl = clampInt(0, 29, startLayer);
  const candidates: { idx: number; rgb: RGB }[] = [];
  for (let i = sl; i < LB_RGB.length; i++) candidates.push({ idx: i, rgb: LB_RGB[i] });

  const byCount = src.slice().sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    if (a.rgb[0] !== b.rgb[0]) return a.rgb[0] - b.rgb[0];
    if (a.rgb[1] !== b.rgb[1]) return a.rgb[1] - b.rgb[1];
    return a.rgb[2] - b.rgb[2];
  });

  const used = new Set<number>();
  const result: PalettePair[] = [];
  for (const s of byCount) {
    const ranked = candidates
      .slice()
      .sort((x, y) => {
        const dx = dist2(s.rgb, x.rgb);
        const dy = dist2(s.rgb, y.rgb);
        if (dx !== dy) return dx - dy;
        return x.idx - y.idx;
      });
    let picked: { idx: number; rgb: RGB } | null = null;
    for (const cand of ranked) {
      if (!used.has(cand.idx)) {
        picked = cand;
        break;
      }
    }
    if (!picked) break;
    used.add(picked.idx);
    result.push({ svg: picked.rgb, target: s.rgb, count: s.count });
  }
  return result.slice(0, mc);
}

function sortKey(rgb: RGB, count: number, mode: LayerSortMode): number[] {
  const lum = luminance(rgb);
  const h = hue(rgb);
  switch (mode) {
    case "light-to-dark":
      return [-lum, h, -count];
    case "most-used":
      return [-count, lum, h];
    case "hue":
      return [h, lum, -count];
    case "dark-to-light":
    default:
      return [lum, h, -count];
  }
}

function sortPairs(pairs: PalettePair[], mode: LayerSortMode): void {
  const keyed = pairs.map((p) => ({ p, key: sortKey(p.svg, p.count, mode) }));
  keyed.sort((a, b) => {
    for (let i = 0; i < a.key.length; i++) {
      if (a.key[i] !== b.key[i]) return a.key[i] < b.key[i] ? -1 : 1;
    }
    // Fully deterministic tiebreak so equal keys never depend on sort stability.
    if (a.p.svg[0] !== b.p.svg[0]) return a.p.svg[0] - b.p.svg[0];
    if (a.p.svg[1] !== b.p.svg[1]) return a.p.svg[1] - b.p.svg[1];
    if (a.p.svg[2] !== b.p.svg[2]) return a.p.svg[2] - b.p.svg[2];
    if (a.p.target[0] !== b.p.target[0]) return a.p.target[0] - b.p.target[0];
    if (a.p.target[1] !== b.p.target[1]) return a.p.target[1] - b.p.target[1];
    return a.p.target[2] - b.p.target[2];
  });
  for (let i = 0; i < keyed.length; i++) pairs[i] = keyed[i].p;
}

/**
 * Shared quantization used by both the photo workflow and photo-fill:
 * median-cut RGB colors (<= maxColors) over the given samples, then palette
 * choice per mode. Returns [svgColor, targetColor] pairs ordered by sortMode.
 */
export function quantizeColors(
  rgbSamples: Uint8Array | Uint8ClampedArray,
  sampleCount: number,
  maxColors: number,
  paletteMode: PaletteMode,
  sortMode: LayerSortMode,
  startLayer: number,
): { svgColor: string; targetColor: string }[] {
  const source = buildSourcePalette(rgbSamples, sampleCount, maxColors);
  if (source.length === 0) return [];
  const pairs = choosePalette(source, maxColors, paletteMode, startLayer);
  sortPairs(pairs, sortMode);
  return pairs.map((p) => ({ svgColor: rgbToHex(p.svg), targetColor: rgbToHex(p.target) }));
}

// ---------- Floyd–Steinberg dithering ----------

/**
 * Floyd–Steinberg serpentine error diffusion against a fixed palette; returns
 * per-sample palette indices (-1 = skipped). Skipped cells absorb incoming
 * error and propagate none, so masked background never bleeds into visible dots.
 * DEVIATION: the Python app used Pillow's raster-order FS; serpentine is the
 * requested upgrade and is symmetric across rows.
 */
export function ditherToPalette(
  rgba: Uint8ClampedArray,
  width: number,
  height: number,
  palette: readonly [number, number, number][],
  options: { dither: boolean; skipMask?: Uint8Array; hooks?: WorkHooks },
): Int16Array {
  const n = width * height;
  const out = new Int16Array(n);
  const skip = options.skipMask;
  const pn = palette.length;
  const onRow = options.hooks?.onRow;
  const isCancelled = options.hooks?.isCancelled;
  if (pn === 0) {
    out.fill(-1);
    return out;
  }

  const nearest = (r: number, g: number, b: number): number => {
    let bi = 0;
    let bd = Infinity;
    for (let k = 0; k < pn; k++) {
      const pc = palette[k];
      const dr = r - pc[0];
      const dg = g - pc[1];
      const db = b - pc[2];
      const d = 0.3 * dr * dr + 0.59 * dg * dg + 0.11 * db * db;
      if (d < bd) {
        bd = d;
        bi = k;
      }
    }
    return bi;
  };

  if (!options.dither) {
    for (let i = 0; i < n; i++) {
      if (isCancelled && (i & 32767) === 0 && isCancelled()) {
        throw new PhotoCancelled("Photo layer generation cancelled.");
      }
      if (onRow && (i & 32767) === 0) onRow(n > 0 ? i / n : 1);
      if (skip && skip[i]) {
        out[i] = -1;
        continue;
      }
      const p = i * 4;
      out[i] = nearest(rgba[p], rgba[p + 1], rgba[p + 2]);
    }
    return out;
  }

  const errR = new Float32Array(n);
  const errG = new Float32Array(n);
  const errB = new Float32Array(n);
  let er = 0;
  let eg = 0;
  let eb = 0;
  const diffuse = (nx: number, ny: number, f: number): void => {
    if (nx < 0 || nx >= width || ny < 0 || ny >= height) return;
    const j = ny * width + nx;
    errR[j] += er * f;
    errG[j] += eg * f;
    errB[j] += eb * f;
  };

  for (let y = 0; y < height; y++) {
    if (isCancelled && (y & 15) === 0 && isCancelled()) {
      throw new PhotoCancelled("Photo layer generation cancelled.");
    }
    if (onRow && (y & 15) === 0) onRow(height > 0 ? y / height : 1);
    const l2r = (y & 1) === 0;
    for (let k = 0; k < width; k++) {
      const x = l2r ? k : width - 1 - k;
      const i = y * width + x;
      if (skip && skip[i]) {
        out[i] = -1;
        continue;
      }
      const p = i * 4;
      const r = rgba[p] + errR[i];
      const g = rgba[p + 1] + errG[i];
      const b = rgba[p + 2] + errB[i];
      const idx = nearest(r, g, b);
      out[i] = idx;
      const pc = palette[idx];
      er = r - pc[0];
      eg = g - pc[1];
      eb = b - pc[2];
      const dir = l2r ? 1 : -1;
      diffuse(x + dir, y, 7 / 16);
      diffuse(x - dir, y + 1, 3 / 16);
      diffuse(x, y + 1, 5 / 16);
      diffuse(x + dir, y + 1, 1 / 16);
    }
  }
  return out;
}

// ---------- calibrated (measured-swatch) palette ----------

/** Valid "#RRGGBB" test used to skip malformed measured-color strings. */
function isHexColor(s: string): boolean {
  return /^#?[0-9a-fA-F]{6}$/.test(s.trim());
}

/**
 * The measured-color palette + parameter tuple resolved from an active swatch
 * library. `palette[k]` is the RGB the operator actually achieved; `entries[k]`
 * is the swatch (its CutParams + focus) that produced it. Photo quantization
 * maps every visible dot to one of these achievable colors, so each output
 * layer corresponds to a burnable parameter set.
 */
interface CalibratedPalette {
  palette: RGB[];
  entries: SwatchEntry[];
}

/**
 * Resolve a swatch library into a deterministic calibrated palette. Swatches
 * with a malformed measured color are dropped; exact-duplicate measured colors
 * collapse to the first occurrence (a stable, order-independent sort keeps the
 * result reproducible regardless of library edit order). Non-overlapping by
 * construction — one palette entry per distinct achievable color.
 */
export function resolveCalibratedPalette(library: SwatchLibrary): CalibratedPalette {
  const seen = new Set<string>();
  const kept: SwatchEntry[] = [];
  const sorted = library.swatches.slice().sort((a, b) => {
    const ka = a.measuredColor.trim().toUpperCase();
    const kb = b.measuredColor.trim().toUpperCase();
    if (ka !== kb) return ka < kb ? -1 : 1;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
  for (const sw of sorted) {
    if (!isHexColor(sw.measuredColor)) continue;
    const key = sw.measuredColor.trim().toUpperCase().replace(/^#/, "");
    if (seen.has(key)) continue;
    seen.add(key);
    kept.push(sw);
  }
  return { palette: kept.map((s) => hexToRgb(s.measuredColor)), entries: kept };
}

function paramsToRecord(
  index: number,
  runOrder: number,
  name: string,
  targetColorHex: string,
  params: CutParams,
  focusOffsetMm: number,
): MopaLayerRecord {
  return {
    index,
    runOrder,
    name,
    targetColorHex,
    power: params.power,
    speed: params.speed,
    frequencyKhz: params.frequencyKhz,
    pulseWidthNs: params.qPulseNs,
    hatchSpacingMm: params.intervalMm,
    hatchAngleDeg: 0,
    hatchType: "fill",
    passes: params.passes,
    focusOffsetMm,
  };
}

// ---------- top-level photo layer generation ----------

/**
 * Stage weights for the MONOTONIC overall progress fraction. The heavy stages
 * (resample, dither) dominate; palette build is reported indeterminate (-1)
 * because median-cut cost is data-dependent and not smoothly measurable.
 */
const STAGE = {
  setup: 0.02,
  resample: 0.3,
  read: 0.1,
  palette: 0.18, // reported as -1 while running; band still reserved for ordering
  assign: 0.35,
  emit: 0.05,
} as const;

interface RunOptions {
  /** When present with usable swatches, quantize to the measured palette. */
  swatchLibrary?: SwatchLibrary;
}

/**
 * Shared implementation for adaptive/lightburn and calibrated photo layering.
 * Emits a monotonic overall fraction across weighted stages and a real
 * pixelCount, and threads cooperative cancel into every heavy loop. Returns a
 * CalibratedPhotoResult (mopaRecords is empty unless a usable swatch library
 * was supplied); a plain PhotoResult consumer simply ignores mopaRecords.
 */
function runPhotoLayers(
  image: RawImage,
  settings: PhotoSettings,
  cb: PhotoCallbacks,
  opts: RunOptions,
): CalibratedPhotoResult {
  // Monotonic overall-fraction mapper: each stage owns a contiguous band and
  // its local 0..1 maps into that band, so the bar never resets backward.
  let base = 0;
  const emitCount = { value: 0 };
  const report = (stage: string, band: number, local: number): void => {
    if (!cb.onProgress) return;
    const overall = local < 0 ? -1 : Math.min(0.999, base + band * Math.max(0, Math.min(1, local)));
    cb.onProgress(stage, overall, emitCount.value);
  };
  const advance = (band: number): void => {
    base = Math.min(1, base + band);
  };
  const checkCancel = (): void => {
    if (cb.isCancelled?.()) throw new PhotoCancelled("Photo layer generation cancelled.");
  };

  let maxLayers = clampInt(1, 30, settings.maxLayers);
  const startLayer = clampInt(0, 29, settings.startLayer);
  maxLayers = Math.max(1, Math.min(maxLayers, 30 - startLayer));
  const outputWidthMm = Math.max(0.001, settings.outputWidthMm);
  const pixelSize = Math.max(0.001, settings.pixelSize);
  const spacingX = Math.max(0.001, settings.spacingX);
  const spacingY = Math.max(0.001, settings.spacingY);
  const maxPixels = Math.max(1, Math.floor(settings.maxPixels));
  const isLightburn = settings.paletteMode === "lightburn";

  const calibrated =
    opts.swatchLibrary && opts.swatchLibrary.swatches.length > 0
      ? resolveCalibratedPalette(opts.swatchLibrary)
      : null;
  const useCalibrated = calibrated !== null && calibrated.palette.length > 0;

  report("Opening image", STAGE.setup, 0);
  checkCancel();
  const srcW = image.width | 0;
  const srcH = image.height | 0;
  if (srcW <= 0 || srcH <= 0 || image.data.length < srcW * srcH * 4) {
    throw new Error("Image has no usable pixel data.");
  }
  advance(STAGE.setup);

  const notes: string[] = [];
  const width0 = Math.max(spacingX, outputWidthMm);
  const height0 = width0 * (srcH / srcW);
  let cols = Math.max(1, Math.round(width0 / spacingX));
  let rows = Math.max(1, Math.round(height0 / spacingY));
  if (cols * rows > maxPixels) {
    const scale = Math.sqrt(maxPixels / (cols * rows));
    const oldCols = cols;
    const oldRows = rows;
    cols = Math.max(1, Math.floor(cols * scale));
    rows = Math.max(1, Math.floor(rows * scale));
    notes.push(
      `Grid reduced from ${oldCols}x${oldRows} to ${cols}x${rows} to stay under the max-pixels limit.`,
    );
  }
  const widthMm = cols * spacingX;
  const heightMm = rows * spacingY;
  const n = cols * rows;
  emitCount.value = n;

  checkCancel();
  const grid = resampleAreaAverage(image.data, srcW, srcH, cols, rows, {
    onRow: (f) => report(`Resampling to ${cols} x ${rows}`, STAGE.resample, f),
    isCancelled: cb.isCancelled,
  });
  advance(STAGE.resample);

  const skipMask = new Uint8Array(n);
  const samples = new Uint8Array(n * 3);
  let visibleCount = 0;
  let ignoredCount = 0;
  const ignoreTransparent = settings.ignoreTransparent;
  const ignoreNearWhite = settings.ignoreNearWhite;
  for (let i = 0; i < n; i++) {
    if (i && i % 30000 === 0) {
      checkCancel();
      report("Reading pixels", STAGE.read, i / n);
    }
    const p = i * 4;
    const r = grid[p];
    const g = grid[p + 1];
    const b = grid[p + 2];
    const a = grid[p + 3];
    let ignore = false;
    if (ignoreTransparent && a <= ALPHA_THRESHOLD) ignore = true;
    if (ignoreNearWhite && r >= WHITE_THRESHOLD && g >= WHITE_THRESHOLD && b >= WHITE_THRESHOLD)
      ignore = true;
    if (ignore) {
      skipMask[i] = 1;
      ignoredCount++;
    } else {
      const sp = visibleCount * 3;
      samples[sp] = r;
      samples[sp + 1] = g;
      samples[sp + 2] = b;
      visibleCount++;
    }
  }
  advance(STAGE.read);
  if (visibleCount === 0) {
    throw new Error(
      "No visible pixels remain. Disable transparent/near-white filtering or choose a different image.",
    );
  }

  // Palette selection: calibrated measured colors, else median-cut quantization.
  report("Building color palette", STAGE.palette, -1);
  checkCancel();
  const assignPalette: RGB[] = [];
  // Per palette entry: color written to SVG, the represented target color, and
  // (calibrated only) the swatch that supplies its laser parameters.
  const entrySvg: string[] = [];
  const entryTarget: string[] = [];
  const entrySwatch: (SwatchEntry | null)[] = [];

  if (useCalibrated && calibrated) {
    for (let k = 0; k < calibrated.entries.length; k++) {
      const sw = calibrated.entries[k];
      const rgb = calibrated.palette[k];
      assignPalette.push(rgb);
      const hex = rgbToHex(rgb);
      entrySvg.push(hex);
      entryTarget.push(hex);
      entrySwatch.push(sw);
    }
  } else {
    const pairs = quantizeColors(
      samples,
      visibleCount,
      maxLayers,
      settings.paletteMode,
      settings.sortMode,
      startLayer,
    );
    if (pairs.length === 0) {
      throw new Error("Could not build a color palette from this image.");
    }
    for (const pr of pairs) {
      assignPalette.push(hexToRgb(pr.svgColor));
      entrySvg.push(pr.svgColor);
      entryTarget.push(pr.targetColor);
      entrySwatch.push(null);
    }
  }
  advance(STAGE.palette);

  report("Assigning pixels to color layers", STAGE.assign, 0);
  checkCancel();
  const assign = ditherToPalette(grid, cols, rows, assignPalette, {
    dither: settings.dither,
    skipMask,
    hooks: {
      onRow: (f) => report("Assigning pixels to color layers", STAGE.assign, f),
      isCancelled: cb.isCancelled,
    },
  });
  advance(STAGE.assign);
  checkCancel();

  const K = assignPalette.length;
  const counts = new Array<number>(K).fill(0);
  for (let i = 0; i < n; i++) {
    const idx = assign[i];
    if (idx >= 0 && idx < K) counts[idx]++;
  }

  const remap = new Int16Array(K).fill(-1);
  const used: number[] = [];
  for (let k = 0; k < K; k++) {
    if (counts[k] > 0) {
      remap[k] = used.length;
      used.push(k);
    }
  }

  let assignedCount = 0;
  for (const k of used) assignedCount += counts[k];

  const outGrid = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    const idx = assign[i];
    if (idx >= 0 && idx < K && counts[idx] > 0) outGrid[i] = remap[idx] + 1;
  }

  const palette: PhotoPaletteEntry[] = [];
  const mopaRecords: MopaLayerRecord[] = [];
  for (const oldIdx of used) {
    const newIdx = remap[oldIdx];
    const svgColor = entrySvg[oldIdx];
    const targetColor = entryTarget[oldIdx];
    const svgRgb = hexToRgb(svgColor);
    const targetRgb = hexToRgb(targetColor);
    let layerIndex = startLayer + newIdx;
    if (!useCalibrated && isLightburn) {
      const li = LB_INDEX_BY_HEX.get(svgColor);
      if (li !== undefined) layerIndex = li;
    }
    const count = counts[oldIdx];
    const percent = assignedCount ? (count / assignedCount) * 100 : 0;
    const paletteError = Math.sqrt(dist2(svgRgb, targetRgb));
    palette.push({ layerIndex, svgColor, targetColor, pixelCount: count, percent, paletteError });

    if (useCalibrated) {
      const sw = entrySwatch[oldIdx];
      if (sw) {
        mopaRecords.push(
          paramsToRecord(
            layerIndex,
            mopaRecords.length + 1,
            sw.label || svgColor,
            targetColor,
            sw.params,
            sw.focusOffsetMm,
          ),
        );
      }
    }
  }

  notes.push(
    `Generated ${palette.length} color layer(s) from ${commas(assignedCount)} visible pixel(s).`,
  );
  if (ignoredCount) {
    notes.push(`Ignored ${commas(ignoredCount)} transparent/background pixel(s).`);
  }
  notes.push(
    useCalibrated
      ? "Palette mode uses your measured calibration colors — each layer maps to a burnable parameter set. Verify on a test grid before a production run."
      : isLightburn
        ? "Palette mode uses exact default LightBurn colors for more predictable layer import."
        : "Palette mode uses adaptive photo colors for the closest visual SVG preview.",
  );

  report("Creating vector-dot map", STAGE.emit, 1);
  advance(STAGE.emit);

  return {
    cols,
    rows,
    originX: spacingX / 2,
    originY: spacingY / 2,
    spacingX,
    spacingY,
    pixelSize,
    shape: settings.shape,
    grid: outGrid,
    palette,
    paletteMode: settings.paletteMode,
    sourceName: image.name,
    sourceWidth: srcW,
    sourceHeight: srcH,
    widthMm,
    heightMm,
    visibleCount,
    notes,
    mopaRecords,
  };
}

/**
 * Generate color-separated photo layers using the adaptive or LightBurn palette
 * modes (unchanged public behavior). The worker calls this; passing an
 * onProgress/isCancelled on `cb` is all that is needed to get monotonic progress
 * + cooperative cancel — no signature change required at the call site.
 */
export function generatePhotoLayers(
  image: RawImage,
  settings: PhotoSettings,
  cb: PhotoCallbacks,
): PhotoResult {
  return runPhotoLayers(image, settings, cb, {});
}

/**
 * Generate color-separated photo layers quantized to an operator's measured
 * calibration library. Every emitted layer is strictly non-overlapping (one
 * palette index per dot) and carries a MopaLayerRecord built from the matching
 * swatch's parameters, so exports can travel with a reproducible per-color
 * recipe. Falls back to adaptive/lightburn behavior when the library has no
 * usable swatches (mopaRecords is then empty).
 */
export function generatePhotoLayersCalibrated(
  image: RawImage,
  settings: PhotoSettings,
  swatchLibrary: SwatchLibrary,
  cb: PhotoCallbacks,
): CalibratedPhotoResult {
  return runPhotoLayers(image, settings, cb, { swatchLibrary });
}
